import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import ExpandDiv from "../common/ExpandDiv";
import {
  FormInput,
  FormSelect,
  FormCheckbox,
  FieldTable,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function COCRSP(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(false);

  const handleExpandStatusChange = () => {
    setExpandStatus(!expandStatus);
  };

  const physicalCareOptions = [
    { _id: "Eat Regularly", name: "Eat Regularly" },
    { _id: "Improve Sleep", name: "Improve Sleep" },
    { _id: "Eat more vitamins/nutrients", name: "Eat more vitamins/nutrients" },
    {
      _id: "Wear clothes that make you feel good",
      name: "Wear clothes that make you feel good",
    },
    { _id: "Physical Activity", name: "Physical Activity" },
    {
      _id: "Attend to your routine medical care needs",
      name: "Attend to your routine medical care needs",
    },
    { _id: "Take a break", name: "Take a break" },
    { _id: "Other", name: "Other" },
  ];
  const mentalCareOptions = [
    { _id: "Take day trips or vacations", name: "Take day trips or vacations" },
    { _id: "Try something new", name: "Try something new" },
    { _id: "Disconnect from Technology", name: "Disconnect from Technology" },
    {
      _id: `Say "No" to extra responsibilities`,
      name: `Say "No" to extra responsibilities`,
    },
    { _id: "Make time to self-reflect", name: "Make time to self-reflect" },
    { _id: "Journal", name: "Journal" },
    { _id: "Draw/Art", name: "Draw/Art" },
    {
      _id: "Read something for fun (not related to foster care)",
      name: "Read something for fun (not related to foster care)",
    },
    { _id: "Other", name: "Other" },
  ];
  const emotionalCareOptions = [
    {
      _id: "Socialize/Spend time with others",
      name: "Socialize/Spend time with others",
    },
    {
      _id: "Find things that make me laugh",
      name: "Find things that make me laugh",
    },
    {
      _id: "Keep in contact with important people",
      name: "Keep in contact with important people",
    },
    { _id: "Praise/Affirm myself", name: "Praise/Affirm myself" },
    { _id: "Allow myself to cry", name: "Allow myself to cry" },
    { _id: "Other", name: "Other" },
  ];
  const spiritualCareOptions = [
    { _id: "Spend time in nature", name: "Spend time in nature" },
    {
      _id: "Listen to inspirational podcasts",
      name: "Listen to inspirational podcasts",
    },
    {
      _id: "Identify what is meaningful to me",
      name: "Identify what is meaningful to me",
    },
    { _id: "Pray", name: "Pray" },
    { _id: "Sing", name: "Sing" },
    { _id: "Meditate", name: "Meditate" },
    {
      _id: "Give back to a cause that is important to you",
      name: "Give back to a cause that is important to you",
    },
    { _id: "Other", name: "Other" },
  ];
  const relationshipCareOptions = [
    {
      _id: "Schedule regular dates with your spouse",
      name: "Schedule regular dates with your spouse",
    },
    {
      _id: "Spend intentional time with your animal(s)",
      name: "Spend intentional time with your animal(s)",
    },
    {
      _id: "Schedule activities with your children",
      name: "Schedule activities with your children",
    },
    {
      _id: "Make time to reply to emails, cards, etc.",
      name: "Make time to reply to emails, cards, etc.",
    },
    { _id: "Make time to see friends", name: "Make time to see friends" },
    {
      _id: "Allow others to do things for me",
      name: "Allow others to do things for me",
    },
    { _id: "Expand my social circle", name: "Expand my social circle" },
    { _id: "Check in with a relative", name: "Check in with a relative" },
    {
      _id: "Share your fears/hopes with a close friend or relative",
      name: "Share your fears/hopes with a close friend or relative",
    },
    { _id: "Other", name: "Other" },
  ];
  const roleCareOptions = [
    {
      _id: "Take a break during the day (i.e. lunch)",
      name: "Take a break during the day (i.e. lunch)",
    },
    { _id: "Set limits", name: "Set limits" },
    {
      _id: "Make time to connect with other foster families",
      name: "Make time to connect with other foster families",
    },
    {
      _id: "Create a calming space for you to work and focus",
      name: "Create a calming space for you to work and focus",
    },
    {
      _id: "Make quiet/uninterrupted time to work on projects or tasks",
      name: "Make quiet/uninterrupted time to work on projects or tasks",
    },
    {
      _id: "Communicate your needs with SAFY",
      name: "Communicate your needs with SAFY",
    },
    {
      _id: "Participate in foster parent support group",
      name: "Participate in foster parent support group",
    },
    {
      _id: "Develop a non-trauma area of interest",
      name: "Develop a non-trauma area of interest",
    },
    { _id: "Other", name: "Other" },
  ];

  const customValidate = (errs) => {
    const daytimeRespiteDetails = JSON.parse(
      data.DaytimeRespiteDetails["@value"]
    );

    if (daytimeRespiteDetails.length === 0) {
      if (errs)
        errs.DaytimeRespiteDetails = "Daytime Respite details are required.";
      else
        errs = {
          DaytimeRespiteDetails: "Daytime Respite details are required.",
        };
    } else {
      const respiteWithoutAllFields = daytimeRespiteDetails.filter(
        (detail) =>
          detail.Action === "" ||
          detail.Length === "" ||
          detail.Frequency === ""
      );

      if (respiteWithoutAllFields.length > 0) {
        if (errs)
          errs.DaytimeRespiteDetails =
            "All fields are required for each Daytime Respite.";
        else
          errs = {
            DaytimeRespiteDetails:
              "All fields are required for each Daytime Respite.",
          };
      } else {
        delete errs.DaytimeRespiteDetails;
      }
    }

    const overnightRespiteDetails = JSON.parse(
      data.OvernightRespiteDetails["@value"]
    );

    if (overnightRespiteDetails.length === 0) {
      if (errs)
        errs.OvernightRespiteDetails =
          "Overnight Respite details are required.";
      else
        errs = {
          OvernightRespiteDetails: "Overnight Respite details are required.",
        };
    } else {
      const respiteWithoutAllFields = overnightRespiteDetails.filter(
        (detail) =>
          detail.Action === "" ||
          detail.Length === "" ||
          detail.Frequency === ""
      );

      if (respiteWithoutAllFields.length > 0) {
        if (errs)
          errs.OvernightRespiteDetails =
            "All fields are required for each Overnight Respite.";
        else
          errs = {
            OvernightRespiteDetails:
              "All fields are required for each Overnight Respite.",
          };
      } else {
        delete errs.OvernightRespiteDetails;
      }
    }

    console.log("CustomErrors: ", errs);
    setErrors(errs);
    return errs;
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <button
                className="btn btn-primary mb-2"
                onClick={(e) => {
                  handleExpandStatusChange();
                }}
              >
                {expandStatus === true ? "Collapse All" : "Expand All"}
              </button>
              <ExpandDiv
                id="RespitePlan"
                expandStatus={expandStatus}
                className="mt-2"
                title="Respite Plan"
              >
                <p>
                  You have probably heard the analogy when flying, "You must put
                  on your oxygen mask before helping others or children with
                  their masks". This applies to your self-care as a caregiver,
                  especially when parenting a traumatized child. You have one of
                  the hardest jobs and it is imperative that caregivers like
                  yourself have time away from the youth to recharge yourselves!
                  This break inevitably helps the youth achieve his/her goals
                  and permanency.
                </p>
                <p>
                  Please complete this Respite Support Plan, personalized to you
                  and the youth in your home. Per Volume 7 Regulations, you have
                  most flexibility to elect adult caregivers of your choice for
                  any caregiving needs under 8 hours. For any overnight respites
                  or more than 8 hours during the day, care must be provided in
                  a licensed foster home OR be approved by SAFY and DHS (along
                  with background checks and CPR/1st Aid).
                </p>
                <FieldTable
                  name="DaytimeRespiteDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Daytime Respite</div>
                      <div className="col border-end py-2">Length of Time</div>
                      <div className="col py-2">Frequency</div>
                    </div>
                    {data.DaytimeRespiteDetails["@value"] &&
                      JSON.parse(data.DaytimeRespiteDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "DaytimeRespiteDetails"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "DaytimeRespiteDetails"
                                      );
                                    }}
                                  ></i>
                                  <FormInput
                                    name="DaytimeRespite"
                                    value={item.DaytimeRespite}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "DaytimeRespiteDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "DaytimeRespiteDetails"
                                      );
                                    }}
                                  ></i>
                                  {item.DaytimeRespite}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Length"
                                  value={item.Length}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "DaytimeRespiteDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                  placeholder="3-8 hrs"
                                />
                              ) : (
                                item.Length
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Frequency"
                                  value={item.Frequency}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "DaytimeRespiteDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                  placeholder="Once a month"
                                />
                              ) : (
                                item.Frequency
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled && (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("DaytimeRespiteDetails", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add another respite plan
                    </button>
                  )}
                  {errors.DaytimeRespiteDetails && (
                    <div className="alert alert-danger">
                      {errors.DaytimeRespiteDetails}
                    </div>
                  )}
                </FieldTable>
                <FieldTable
                  name="OvernightRespiteDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">
                        Overnight Respite
                      </div>
                      <div className="col border-end py-2">Length of Time</div>
                      <div className="col py-2">Frequency</div>
                    </div>
                    {data.OvernightRespiteDetails["@value"] &&
                      JSON.parse(data.OvernightRespiteDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "OvernightRespiteDetails"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "OvernightRespiteDetails"
                                      );
                                    }}
                                  ></i>
                                  <FormInput
                                    name="OvernightRespite"
                                    value={item.OvernightRespite}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "OvernightRespiteDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                    placeholder="Ex: Aunt Sherly"
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "OvernightRespiteDetails"
                                      );
                                    }}
                                  ></i>
                                  {item.OvernightRespite}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Length"
                                  value={item.Length}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "OvernightRespiteDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                  placeholder="2 nights"
                                />
                              ) : (
                                item.Length
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Frequency"
                                  value={item.Frequency}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "OvernightRespiteDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                  placeholder="Every other month"
                                />
                              ) : (
                                item.Frequency
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled && (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("OvernightRespiteDetails", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add another respite plan
                    </button>
                  )}
                  {errors.OvernightRespiteDetails && (
                    <div className="alert alert-danger">
                      {errors.OvernightRespiteDetails}
                    </div>
                  )}
                </FieldTable>
              </ExpandDiv>
              <ExpandDiv
                id="SelfCarePlan"
                expandStatus={expandStatus}
                className="mt-2"
                title="Self-Care Plan"
              >
                <FormCheckbox
                  name="TwoCaregivers"
                  label="There are two caregivers in our home"
                  checked={data.TwoCaregivers["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="ms-3"
                />
                <p>
                  Sometimes even when you have regularly scheduled breaks, you
                  still become overwhelmed and exhausted. And that is okay! You
                  are navigating a lot as a caregiver. Below is a self-care
                  plan, specifically made for caregivers, in order to help
                  identify when and how additional support is needed for you and
                  the youth.
                </p>
                <FormInput
                  name="CaregiverName"
                  label="Caregiver Name"
                  error={errors.CaregiverName}
                  value={data.CaregiverName["@value"]}
                  onChange={handleChange}
                  identical={compare("CaregiverName")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <strong>
                  Identify your signals/signs that you are overwhelmed,
                  exhausted, or burned out:
                </strong>
                <div className="row ms-3">
                  <div className="col">
                    <FormCheckbox
                      name="Sad"
                      label="I feel sad most of the time"
                      checked={data.Sad["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="Discouraged"
                      label="I feel discouraged"
                      checked={data.Discouraged["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="Failure"
                      label="I feel like a failure"
                      checked={data.Failure["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row ms-3">
                  <div className="col">
                    <FormCheckbox
                      name="SleepDisturbances"
                      label="I sleep more/less"
                      checked={data.SleepDisturbances["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="Forgetful"
                      label="I'm forgetful"
                      checked={data.Forgetful["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="Unproductive"
                      label="I'm not as productive"
                      checked={data.Unproductive["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row ms-3">
                  <div className="col">
                    <FormCheckbox
                      name="Tired"
                      label="I'm more tired"
                      checked={data.Tired["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="Pain"
                      label="I feel physical pain in my body"
                      checked={data.Pain["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="EatingHabits"
                      label="I eat more/less"
                      checked={data.EatingHabits["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row ms-3">
                  <div className="col">
                    <FormCheckbox
                      name="Guilt"
                      label="I feel guilty"
                      checked={data.Guilt["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="Irritable"
                      label="I'm more irritable"
                      checked={data.Irritable["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="Startle"
                      label="I startle easily"
                      checked={data.Startle["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row ms-3">
                  <div className="col">
                    <FormCheckbox
                      name="ProblemTrouble"
                      label="I have trouble solving problems"
                      checked={data.ProblemTrouble["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="LoseInterest"
                      label="I lose interest in people/places/things I once enjoyed"
                      checked={data.LoseInterest["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="OtherSign"
                      label="Other"
                      checked={data.OtherSign["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherSign["@value"] === "true" ? (
                      <FormInput
                        name="OtherSignDetails"
                        error={errors.OtherSignDetails}
                        value={data.OtherSignDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherSignDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                </div>
                {errors.Signals && (
                  <div className="alert alert-danger">{errors.Signals}</div>
                )}
                <div>
                  <FieldTable
                    name="PhysicalCareDetails"
                    commentsHandler={handleComments}
                  >
                    <strong>
                      What can you do to care for your physical self?
                    </strong>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Action</div>
                        <div className="col py-2">
                          How often and/or when can you dedicate time to doing
                          this?
                        </div>
                      </div>
                      {data.PhysicalCareDetails["@value"] &&
                        JSON.parse(data.PhysicalCareDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "PhysicalCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "PhysicalCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <FormSelect
                                      name="Action"
                                      value={item.Action}
                                      options={physicalCareOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "PhysicalCareDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {item.Action === "Other" ? (
                                      <FormInput
                                        name="OtherDetails"
                                        value={item.OtherDetails}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "PhysicalCareDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "PhysicalCareDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.Action === "Other"
                                      ? item.OtherDetails
                                      : item.Action}
                                  </>
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Frequency"
                                    value={item.Frequency}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "PhysicalCareDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Frequency
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("PhysicalCareDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add another action
                      </button>
                    )}
                    {errors.PhysicalCareDetails && (
                      <div className="alert alert-danger">
                        {errors.PhysicalCareDetails}
                      </div>
                    )}
                  </FieldTable>
                </div>
                <div>
                  <FieldTable
                    name="MentalCareDetails"
                    commentsHandler={handleComments}
                  >
                    <strong>
                      What can you do to care for your mind/mental health?
                    </strong>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Action</div>
                        <div className="col py-2">
                          How often and/or when can you dedicate time to doing
                          this?
                        </div>
                      </div>
                      {data.MentalCareDetails["@value"] &&
                        JSON.parse(data.MentalCareDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "MentalCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "MentalCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <FormSelect
                                      name={`Action`}
                                      value={item.Action}
                                      options={mentalCareOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalCareDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "MentalCareDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.Action}
                                  </>
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Frequency"
                                    value={item.Frequency}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "MentalCareDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Frequency
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("MentalCareDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add another action
                      </button>
                    )}
                    {errors.MentalCareDetails && (
                      <div className="alert alert-danger">
                        {errors.MentalCareDetails}
                      </div>
                    )}
                  </FieldTable>
                </div>
                <div>
                  <FieldTable
                    name="EmotionalCareDetails"
                    commentsHandler={handleComments}
                  >
                    <strong>
                      What can you do to care for your emotional health?
                    </strong>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Action</div>
                        <div className="col py-2">
                          How often and/or when can you dedicate time to doing
                          this?
                        </div>
                      </div>
                      {data.EmotionalCareDetails["@value"] &&
                        JSON.parse(data.EmotionalCareDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "EmotionalCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "EmotionalCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <FormSelect
                                      name="Action"
                                      value={item.Action}
                                      options={emotionalCareOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "EmotionalCareDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {item.Action === "Other" ? (
                                      <FormInput
                                        name="OtherDetails"
                                        value={item.OtherDetails}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "EmotionalCareDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "EmotionalCareDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.Action === "Other"
                                      ? item.OtherDetails
                                      : item.Action}
                                  </>
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Frequency"
                                    value={item.Frequency}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "EmotionalCareDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Frequency
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("EmotionalCareDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add another action
                      </button>
                    )}
                    {errors.EmotionalCareDetails && (
                      <div className="alert alert-danger">
                        {errors.EmotionalCareDetails}
                      </div>
                    )}
                  </FieldTable>
                </div>
                <div>
                  <FieldTable
                    name="SpiritualCareDetails"
                    commentsHandler={handleComments}
                  >
                    <strong>
                      What can you do to care for your spiritual health?
                    </strong>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Action</div>
                        <div className="col py-2">
                          How often and/or when can you dedicate time to doing
                          this?
                        </div>
                      </div>
                      {data.SpiritualCareDetails["@value"] &&
                        JSON.parse(data.SpiritualCareDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "SpiritualCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "SpiritualCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <FormSelect
                                      name="Action"
                                      value={item.Action}
                                      options={spiritualCareOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SpiritualCareDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {item.Action === "Other" ? (
                                      <FormInput
                                        name="OtherDetails"
                                        value={item.OtherDetails}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SpiritualCareDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "SpiritualCareDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.Action === "Other"
                                      ? item.OtherDetails
                                      : item.Action}
                                  </>
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Frequency"
                                    value={item.Frequency}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "SpiritualCareDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Frequency
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("SpiritualCareDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add another action
                      </button>
                    )}
                    {errors.SpiritualCareDetails && (
                      <div className="alert alert-danger">
                        {errors.SpiritualCareDetails}
                      </div>
                    )}
                  </FieldTable>
                </div>
                <div>
                  <FieldTable
                    name="RelationshipCareDetails"
                    commentsHandler={handleComments}
                  >
                    <strong>
                      What can you do to care for your relationships?
                    </strong>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Action</div>
                        <div className="col py-2">
                          How often and/or when can you dedicate time to doing
                          this?
                        </div>
                      </div>
                      {data.RelationshipCareDetails["@value"] &&
                        JSON.parse(data.RelationshipCareDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "RelationshipCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "RelationshipCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <FormSelect
                                      name="Action"
                                      value={item.Action}
                                      options={relationshipCareOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "RelationshipCareDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {item.Action === "Other" ? (
                                      <FormInput
                                        name="OtherDetails"
                                        value={item.OtherDetails}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "RelationshipCareDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "RelationshipCareDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.Action === "Other"
                                      ? item.OtherDetails
                                      : item.Action}
                                  </>
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Frequency"
                                    value={item.Frequency}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "RelationshipCareDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Frequency
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("RelationshipCareDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add another action
                      </button>
                    )}
                    {errors.RelationshipCareDetails && (
                      <div className="alert alert-danger">
                        {errors.RelationshipCareDetails}
                      </div>
                    )}
                  </FieldTable>
                </div>
                <div>
                  <FieldTable
                    name="RoleCareDetails"
                    commentsHandler={handleComments}
                  >
                    <strong>
                      What can you do to care for your role as a caregiver?
                    </strong>
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Action</div>
                        <div className="col py-2">
                          How often and/or when can you dedicate time to doing
                          this?
                        </div>
                      </div>
                      {data.RoleCareDetails["@value"] &&
                        JSON.parse(data.RoleCareDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(index, "RoleCareDetails");
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "RoleCareDetails"
                                        );
                                      }}
                                    ></i>
                                    <FormSelect
                                      name="Action"
                                      value={item.Action}
                                      options={roleCareOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "RoleCareDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {item.Action === "Other" ? (
                                      <FormInput
                                        name="OtherDetails"
                                        value={item.OtherDetails}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "RoleCareDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(index, "RoleCareDetails");
                                      }}
                                    ></i>
                                    {item.Action === "Other"
                                      ? item.OtherDetails
                                      : item.Action}
                                  </>
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Frequency"
                                    value={item.Frequency}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "RoleCareDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Frequency
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("RoleCareDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add another action
                      </button>
                    )}
                    {errors.RoleCareDetails && (
                      <div className="alert alert-danger">
                        {errors.RoleCareDetails}
                      </div>
                    )}
                  </FieldTable>
                </div>
                {data.TwoCaregivers["@value"] === "true" ? (
                  <>
                    <br />
                    <FormInput
                      name="CaregiverName2"
                      label="Second Caregiver's Name"
                      error={errors.CaregiverName2}
                      value={data.CaregiverName2["@value"]}
                      onChange={handleChange}
                      identical={compare("CaregiverName2")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <strong>
                      Identify your signals/signs that you are overwhelmed,
                      exhausted, or burned out:
                    </strong>
                    <div className="row ms-3">
                      <div className="col">
                        <FormCheckbox
                          name="Sad2"
                          label="I feel sad most of the time"
                          checked={data.Sad2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="Discouraged2"
                          label="I feel discouraged"
                          checked={data.Discouraged2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="Failure2"
                          label="I feel like a failure"
                          checked={data.Failure2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row ms-3">
                      <div className="col">
                        <FormCheckbox
                          name="SleepDisturbances2"
                          label="I sleep more/less"
                          checked={data.SleepDisturbances2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="Forgetful2"
                          label="I'm forgetful"
                          checked={data.Forgetful2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="Unproductive2"
                          label="I'm not as productive"
                          checked={data.Unproductive2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row ms-3">
                      <div className="col">
                        <FormCheckbox
                          name="Tired2"
                          label="I'm more tired"
                          checked={data.Tired2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="Pain2"
                          label="I feel physical pain in my body"
                          checked={data.Pain2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="EatingHabits2"
                          label="I eat more/less"
                          checked={data.EatingHabits2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row ms-3">
                      <div className="col">
                        <FormCheckbox
                          name="Guilt2"
                          label="I feel guilty"
                          checked={data.Guilt2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="Irritable2"
                          label="I'm more irritable"
                          checked={data.Irritable2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="Startle2"
                          label="I startle easily"
                          checked={data.Startle2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row ms-3">
                      <div className="col">
                        <FormCheckbox
                          name="ProblemTrouble2"
                          label="I have trouble solving problems"
                          checked={data.ProblemTrouble2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="LoseInterest2"
                          label="I lose interest in people/places/things I once enjoyed"
                          checked={data.LoseInterest2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormCheckbox
                          name="OtherSign2"
                          label="Other"
                          checked={data.OtherSign2["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {data.OtherSign2["@value"] === "true" ? (
                          <FormInput
                            name="OtherSignDetails2"
                            error={errors.OtherSignDetails2}
                            value={data.OtherSignDetails2["@value"]}
                            onChange={handleChange}
                            identical={compare("OtherSignDetails2")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    {errors.Signals2 && (
                      <div className="alert alert-danger">
                        {errors.Signals2}
                      </div>
                    )}
                    <div>
                      <FieldTable
                        name="PhysicalCareDetails2"
                        commentsHandler={handleComments}
                      >
                        <strong>
                          What can you do to care for your physical self?
                        </strong>
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col border-end py-2">Action</div>
                            <div className="col py-2">
                              How often and/or when can you dedicate time to
                              doing this?
                            </div>
                          </div>
                          {data.PhysicalCareDetails2["@value"] &&
                            JSON.parse(data.PhysicalCareDetails2["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "PhysicalCareDetails2"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "PhysicalCareDetails2"
                                            );
                                          }}
                                        ></i>
                                        <FormSelect
                                          name="Action"
                                          value={item.Action}
                                          options={physicalCareOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "PhysicalCareDetails2"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        {item.Action === "Other" ? (
                                          <FormInput
                                            name="OtherDetails"
                                            value={item.OtherDetails}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "PhysicalCareDetails2"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : null}
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "PhysicalCareDetails2"
                                            );
                                          }}
                                        ></i>
                                        {item.Action === "Other"
                                          ? item.OtherDetails
                                          : item.Action}
                                      </>
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Frequency"
                                        value={item.Frequency}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "PhysicalCareDetails2"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Frequency
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                        </div>
                        {!formDisabled && (
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("PhysicalCareDetails2", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add another action
                          </button>
                        )}
                        {errors.PhysicalCareDetails2 && (
                          <div className="alert alert-danger">
                            {errors.PhysicalCareDetails2}
                          </div>
                        )}
                      </FieldTable>
                    </div>
                    <div>
                      <FieldTable
                        name="MentalCareDetails2"
                        commentsHandler={handleComments}
                      >
                        <strong>
                          What can you do to care for your mind/mental health?
                        </strong>
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col border-end py-2">Action</div>
                            <div className="col py-2">
                              How often and/or when can you dedicate time to
                              doing this?
                            </div>
                          </div>
                          {data.MentalCareDetails2["@value"] &&
                            JSON.parse(data.MentalCareDetails2["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "MentalCareDetails2"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "MentalCareDetails2"
                                            );
                                          }}
                                        ></i>
                                        <FormSelect
                                          name="Action"
                                          value={item.Action}
                                          options={mentalCareOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "MentalCareDetails2"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        {item.Action === "Other" ? (
                                          <FormInput
                                            name="OtherDetails"
                                            value={item.OtherDetails}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "MentalCareDetails2"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : null}
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "MentalCareDetails2"
                                            );
                                          }}
                                        ></i>
                                        {item.Action === "Other"
                                          ? item.OtherDetails
                                          : item.Action}
                                      </>
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Frequency"
                                        value={item.Frequency}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MentalCareDetails2"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Frequency
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                        </div>
                        {!formDisabled && (
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("MentalCareDetails2", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add another action
                          </button>
                        )}
                        {errors.MentalCareDetails2 && (
                          <div className="alert alert-danger">
                            {errors.MentalCareDetails2}
                          </div>
                        )}
                      </FieldTable>
                    </div>
                    <div>
                      <FieldTable
                        name="EmotionalCareDetails2"
                        commentsHandler={handleComments}
                      >
                        <strong>
                          What can you do to care for your emotional health?
                        </strong>
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col border-end py-2">Action</div>
                            <div className="col py-2">
                              How often and/or when can you dedicate time to
                              doing this?
                            </div>
                          </div>
                          {data.EmotionalCareDetails2["@value"] &&
                            JSON.parse(
                              data.EmotionalCareDetails2["@value"]
                            ).map((item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "EmotionalCareDetails2"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "EmotionalCareDetails2"
                                          );
                                        }}
                                      ></i>
                                      <FormSelect
                                        name="Action"
                                        value={item.Action}
                                        options={emotionalCareOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "EmotionalCareDetails2"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                      {item.Action === "Other" ? (
                                        <FormInput
                                          name="OtherDetails"
                                          value={item.OtherDetails}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "EmotionalCareDetails2"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : null}
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "EmotionalCareDetails2"
                                          );
                                        }}
                                      ></i>
                                      {item.Action === "Other"
                                        ? item.OtherDetails
                                        : item.Action}
                                    </>
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Frequency"
                                      value={item.Frequency}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "EmotionalCareDetails2"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Frequency
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                        {!formDisabled && (
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("EmotionalCareDetails2", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add another action
                          </button>
                        )}
                        {errors.EmotionalCareDetails2 && (
                          <div className="alert alert-danger">
                            {errors.EmotionalCareDetails2}
                          </div>
                        )}
                      </FieldTable>
                    </div>
                    <div>
                      <FieldTable
                        name="SpiritualCareDetails2"
                        commentsHandler={handleComments}
                      >
                        <strong>
                          What can you do to care for your spiritual health?
                        </strong>
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col border-end py-2">Action</div>
                            <div className="col py-2">
                              How often and/or when can you dedicate time to
                              doing this?
                            </div>
                          </div>
                          {data.SpiritualCareDetails2["@value"] &&
                            JSON.parse(
                              data.SpiritualCareDetails2["@value"]
                            ).map((item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "SpiritualCareDetails2"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "SpiritualCareDetails2"
                                          );
                                        }}
                                      ></i>
                                      <FormSelect
                                        name="Action"
                                        value={item.Action}
                                        options={spiritualCareOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SpiritualCareDetails2"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                      {item.Action === "Other" ? (
                                        <FormInput
                                          name="OtherDetails"
                                          value={item.OtherDetails}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SpiritualCareDetails2"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : null}
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "SpiritualCareDetails2"
                                          );
                                        }}
                                      ></i>
                                      {item.Action === "Other"
                                        ? item.OtherDetails
                                        : item.Action}
                                    </>
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Frequency"
                                      value={item.Frequency}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SpiritualCareDetails2"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Frequency
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                        {!formDisabled && (
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("SpiritualCareDetails2", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add another action
                          </button>
                        )}
                        {errors.SpiritualCareDetails2 && (
                          <div className="alert alert-danger">
                            {errors.SpiritualCareDetails2}
                          </div>
                        )}
                      </FieldTable>
                    </div>
                    <div>
                      <FieldTable
                        name="RelationshipCareDetails2"
                        commentsHandler={handleComments}
                      >
                        <strong>
                          What can you do to care for your relationships?
                        </strong>
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col border-end py-2">Action</div>
                            <div className="col py-2">
                              How often and/or when can you dedicate time to
                              doing this?
                            </div>
                          </div>
                          {data.RelationshipCareDetails2["@value"] &&
                            JSON.parse(
                              data.RelationshipCareDetails2["@value"]
                            ).map((item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "RelationshipCareDetails2"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "RelationshipCareDetails2"
                                          );
                                        }}
                                      ></i>
                                      <FormSelect
                                        name="Action"
                                        value={item.Action}
                                        options={relationshipCareOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "RelationshipCareDetails2"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                      {item.Action === "Other" ? (
                                        <FormInput
                                          name="OtherDetails"
                                          value={item.OtherDetails}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "RelationshipCareDetails2"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : null}
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "RelationshipCareDetails2"
                                          );
                                        }}
                                      ></i>
                                      {item.Action === "Other"
                                        ? item.OtherDetails
                                        : item.Action}
                                    </>
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Frequency"
                                      value={item.Frequency}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "RelationshipCareDetails2"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Frequency
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                        {!formDisabled && (
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("RelationshipCareDetails2", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add another action
                          </button>
                        )}
                        {errors.RelationshipCareDetails2 && (
                          <div className="alert alert-danger">
                            {errors.RelationshipCareDetails2}
                          </div>
                        )}
                      </FieldTable>
                    </div>
                    <div>
                      <FieldTable
                        name="RoleCareDetails2"
                        commentsHandler={handleComments}
                      >
                        <strong>
                          What can you do to care for your role as a caregiver?
                        </strong>
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col border-end py-2">Action</div>
                            <div className="col py-2">
                              How often and/or when can you dedicate time to
                              doing this?
                            </div>
                          </div>
                          {data.RoleCareDetails2["@value"] &&
                            JSON.parse(data.RoleCareDetails2["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "RoleCareDetails2"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "RoleCareDetails2"
                                            );
                                          }}
                                        ></i>
                                        <FormSelect
                                          name="Action"
                                          value={item.Action}
                                          options={roleCareOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "RoleCareDetails2"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        {item.Action === "Other" ? (
                                          <FormInput
                                            name="OtherDetails"
                                            value={item.OtherDetails}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "RoleCareDetails2"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : null}
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "RoleCareDetails2"
                                            );
                                          }}
                                        ></i>
                                        {item.Action === "Other"
                                          ? item.OtherDetails
                                          : item.Action}
                                      </>
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Frequency"
                                        value={item.Frequency}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "RoleCareDetails2"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Frequency
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                        </div>
                        {!formDisabled && (
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("RoleCareDetails2", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add another action
                          </button>
                        )}
                        {errors.RoleCareDetails2 && (
                          <div className="alert alert-danger">
                            {errors.RoleCareDetails2}
                          </div>
                        )}
                      </FieldTable>
                    </div>
                  </>
                ) : null}
              </ExpandDiv>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default COCRSP;
